import { useState } from 'react';
import useAvailableDoses from '~/hooks/dose/useAvailableDoses';
import { useCurrentDose } from '~/hooks/dose/useCurrentDose';
import { useReferenceDose } from '~/hooks/dose/useReferenceDose';
import { useSelectedPrimaryImage } from '~/hooks/image/useSelectedImages';

export interface PropagationDoseOption {
    readonly sopInstanceUid: string;
    readonly label?: string;
}

export interface PropagationDoseChoice {
    readonly options: readonly PropagationDoseOption[];
    // null: carry no dose.
    readonly selectedSopInstanceUid: string | null;
    readonly select: (sopInstanceUid: string | null) => void;
}

export function defaultPropagationDose(
    options: readonly PropagationDoseOption[],
    currentSopInstanceUid: string | undefined,
    referenceSopInstanceUid: string | undefined
): string | null {
    const offered = (uid: string | undefined) =>
        !!uid && options.some((option) => option.sopInstanceUid === uid);
    if (offered(currentSopInstanceUid)) {
        return currentSopInstanceUid!;
    }
    return offered(referenceSopInstanceUid) ? referenceSopInstanceUid! : null;
}

export function usePropagationDoseChoice(): PropagationDoseChoice {
    const primaryImage = useSelectedPrimaryImage();
    const currentDose = useCurrentDose();
    const referenceDose = useReferenceDose();
    const options = useAvailableDoses(true)
        .filter((dose) => dose.imageUid === primaryImage?.dicomUid)
        .map((dose) => ({
            sopInstanceUid: dose.sopInstanceUID,
            label: dose.label,
        }));
    const [chosen, setChosen] = useState<string | null | undefined>(undefined);

    const stillOffered =
        chosen === null ||
        options.some((option) => option.sopInstanceUid === chosen);
    const selectedSopInstanceUid =
        chosen !== undefined && stillOffered
            ? chosen
            : defaultPropagationDose(
                  options,
                  currentDose?.sopInstanceUid,
                  referenceDose?.sopInstanceUid
              );

    return { options, selectedSopInstanceUid, select: setChosen };
}
